import { Box } from "@repo/components/src/ui/box";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import type { CampaignTypeOption } from "./campaignTypes";
import { CAMPAIGN_TYPES } from "./campaignTypes";

type Props = {
  onPick: (type: CampaignTypeOption) => void;
};

/**
 * Which kind of campaign to build — the one thing the form-first chat has to know
 * before it can open, because it decides which agent runs and what the Brief asks.
 *
 * Each option is an action — it starts the chat — not a choice that stays selected, so
 * it reads as a button, not a radio. A clickable card rather than a `Button`: it carries
 * a title, a subtitle and a description, which is the rich-children case the styling
 * rules leave to `Pressable`.
 */
const CampaignTypePicker = ({ onPick }: Props) => (
  <VStack space="md" className="w-full max-w-[640px]">
    <VStack space="xs">
      <Text className="text-lg font-semibold text-typography-900">
        ¿Qué tipo de campaña quieres crear?
      </Text>
      <Text className="text-sm text-typography-500">
        Elige el formato y te muestro lo que necesito saber para empezar.
      </Text>
    </VStack>
    <VStack role="group" accessibilityLabel="Tipo de campaña" space="sm">
      {CAMPAIGN_TYPES.map((type) => (
        <Pressable
          key={type.slug}
          role="button"
          onPress={() => onPick(type)}
          accessibilityLabel={`${type.label}. ${type.description}`}
        >
          {({ pressed }) => (
            <Box
              className={`min-h-11 rounded-xl border px-4 py-3 ${
                pressed
                  ? "border-brand-400 bg-brand-50"
                  : "border-outline-100 bg-background-0 hover:border-outline-200 hover:bg-background-50"
              }`}
            >
              <VStack space="xs">
                <Text className="text-sm font-semibold text-typography-900">
                  {type.label}
                  <Text className="text-sm font-normal text-typography-500">
                    {`  ·  ${type.detail}`}
                  </Text>
                </Text>
                <Text className="text-xs text-typography-500">
                  {type.description}
                </Text>
              </VStack>
            </Box>
          )}
        </Pressable>
      ))}
    </VStack>
  </VStack>
);

export default CampaignTypePicker;
